<template>
    <div class="bg-[#66A3FF14] text-white rounded-xl border border-[#FFFFFF1F] p-5 min-h-[334px]">
        <!-- Search Input with Icon -->
        <div class="relative">
            <i class="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"></i>
            <InputText v-model="searchQuery" placeholder="Search Language"
                class="!w-full !pl-10 !pr-4 !py-3.5 !bg-[rgba(255, 255, 255, 0.08)] !border-none !text-[var(--text-gray)] !placeholder-[var(--text-gray)] focus:ring-2 focus:ring-blue-500" />
        </div>

        <!-- Language List -->
        <div ref="scrollContainer" class="mt-4 max-h-56 overflow-y-auto pr-1">
  <div v-for="(language, index) in filteredLanguages" :key="language.code">
    <div
      class="flex items-center gap-3 py-0.5 px-2 cursor-pointer hover:bg-slate-800 rounded-lg"
    >
      <span
        class="flex items-center justify-center w-9 h-9 rounded-full bg-[var(--background-btn)] overflow-hidden"
      >
        <component :is="language.flag" class="w-full h-full" />
      </span>
      <span class="text-base">
        {{ language.name }}
      </span>
    </div>

    <Divider
      unstyled
      v-if="index < filteredLanguages.length - 1"
      class="my-4 border border-[#FFFFFF14]"
    />
  </div>
</div>

    </div>
</template>

<script setup lang="ts">

const USA = defineAsyncComponent(() => import('../atoms/flags/USA.vue'));
const Spain = defineAsyncComponent(() => import('../atoms/flags/Spain.vue'));
const France = defineAsyncComponent(() => import('../atoms/flags/France.vue'));
const Germany = defineAsyncComponent(() => import('../atoms/flags/Germany.vue'));
const India = defineAsyncComponent(() => import('../atoms/flags/India.vue'));
const Portugal = defineAsyncComponent(() => import('../atoms/flags/Portugal.vue'));
const Russia = defineAsyncComponent(() => import('../atoms/flags/Russia.vue'));
const SaudiArabia = defineAsyncComponent(() => import('../atoms/flags/SaudiArabia.vue'));


const searchQuery = ref("");
const scrollContainer = ref<HTMLElement | null>(null);
let animationFrame: number;
let direction = 1; // 1 = down, -1 = up

const languages = ref([
  { code: "en", name: "English", flag: USA, searchTerms: ["english"] },
  { code: "es", name: "Español", flag: Spain, searchTerms: ["spanish", "espanol"] },
  { code: "fr", name: "Français", flag: France, searchTerms: ["french", "francais"] },
  { code: "de", name: "Deutsch", flag: Germany, searchTerms: ["german", "deutsch"] },
  { code: "pt", name: "Português", flag: Portugal, searchTerms: ["portuguese", "portugues"] },
  { code: "hi", name: "हिन्दी", flag: India, searchTerms: ["hindi"] },
  { code: "ar", name: "العربية", flag: SaudiArabia, searchTerms: ["arabic", "arabiya"] },
  { code: "ru", name: "Русский", flag: Russia, searchTerms: ["russian", "russkiy"] },
]);


const filteredLanguages = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();
  if (!query) return languages.value;

  return languages.value.filter((lang) => {
    const nameMatch = lang.name.normalize("NFC").toLocaleLowerCase().includes(query);
    const termMatch = lang.searchTerms.some((term) => term.includes(query));
    return nameMatch || termMatch;
  });
});


// Auto scroll
const autoScroll = () => {
    if (!scrollContainer.value) return;
    const container = scrollContainer.value;
    container.scrollTop += direction * 0.5; // speed

    if (container.scrollTop >= container.scrollHeight - container.clientHeight) {
        direction = -1;
    }
    if (container.scrollTop <= 0) {
        direction = 1;
    }
    animationFrame = requestAnimationFrame(autoScroll);
};

onMounted(() => {
    animationFrame = requestAnimationFrame(autoScroll);
});

onBeforeUnmount(() => {
    cancelAnimationFrame(animationFrame);
});
</script>

<style scoped>
/* Slim scrollbar */
::-webkit-scrollbar {
    width: 4px;
}

::-webkit-scrollbar-thumb {
    background: #475569;
    border-radius: 9999px;
    min-height: 20px;
}

::-webkit-scrollbar-track {
    background: transparent;
}
</style>
